import { useEffect, useMemo, useRef } from "react"

import { saveAs } from "file-saver"
import { asBlob } from "html-docx-js-typescript"
import MarkdownIt from "markdown-it"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SystemMessage } from "../components/system-message"
import type { CustomerConfig } from "@/types/customer-config"

import { downloadResponseApi } from "@/api/api"
import customerConfig from "@/customer/config.json"
import { formatDocumentIntelligenceContent } from "@/features/chat/utils/document-intelligence"
import {
  dashSpecStatusI18nKey,
  isDashChatModule,
  stripDashSpecFences,
} from "@/features/dash/utils/dash-spec-fences"
import { logger } from "@/lib/logger"
import { getInjectedAppName } from "@/lib/runtime-branding"
import { ChatService } from "@/services/chat-service"
import { SettingsService } from "@/services/settings-service"
import { type NavigatorStore, useNavigatorStore } from "@/stores/navigator-store"
import { KNOWN_SESSION_TYPES } from "@/types"
import {
  getDocumentReferences,
  getDocuments,
  getFollowupQuestions,
  getResponseContent,
  getResponseContext,
} from "@/utils/response-format-utils"

import { parseAnswerToHtml, replaceCitationsWithLinks } from "../components/answer-parser"
import { hasMermaidBlocks, replaceMermaidWithImages } from "../utils/mermaid-to-image"

const DOWNLOAD_FILENAME_PREFIX = `${getInjectedAppName() ?? (customerConfig as CustomerConfig).config.app_name ?? "Hello AI"} - `
const MAX_FILENAME_TOTAL_LENGTH = 255

function sanitizeFilename(name: string | null | undefined): string {
  if (!name) {
    return ""
  }

  return name
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .replace(/[.]+$/g, "")
    .trim()
}

function getSafeFilename(preferredName: string, fallbackName: string): string {
  const sanitizedPreferred = sanitizeFilename(preferredName)
  if (sanitizedPreferred) {
    return sanitizedPreferred
  }

  const sanitizedFallback = sanitizeFilename(fallbackName)
  if (sanitizedFallback) {
    return sanitizedFallback
  }

  return "Response"
}

function ensureFilenameLimit(baseName: string, extension: string): string {
  const normalizedExt = extension ? `.${extension.replace(/^\./, "")}` : ""
  const maxBaseLength = Math.max(1, MAX_FILENAME_TOTAL_LENGTH - normalizedExt.length)
  if (baseName.length <= maxBaseLength) {
    return baseName
  }
  return baseName.slice(0, maxBaseLength)
}

export function useSystemMessage({
  answer,
  isStreaming,
  noCitation,
  citationSection,
  customFileName,
  moduleName,
  targetLanguage,
  sessionId,
  sessionType = "free",
  index,
}: SystemMessage & { onCitationNumberClicked?: (index: number) => void }) {
  const { t } = useTranslation()

  const navigatorStore = useNavigatorStore(sessionType)
  const addCitations = navigatorStore((state: NavigatorStore) => state.addCitations)
  const mergeCitationLibraryDocuments = navigatorStore(
    (state: NavigatorStore) => state.mergeCitationLibraryDocuments
  )

  const settingsService = SettingsService.getInstance()

  const answerTextRef = useRef<HTMLDivElement>(null)
  const chatServiceRef = useRef(ChatService.getInstance())

  const responseContext = useMemo(() => getResponseContext(answer), [answer])
  const effectiveSessionId = sessionId ?? responseContext.session_id
  const documents = useMemo(() => getDocuments(answer), [answer])

  // If we have an error, just return empty content
  if ("error" in answer) {
    return {
      answerTextRef,
      parsedAnswer: { answerHtml: "", citations: [] },
      followupQuestions: null,
      documents: undefined,
      documentReferences: undefined,
      citationSection,
      handleCopyRawToClipboardClicked: () => {},
      handleCopyToClipboardClicked: () => {},
      handleDownloadClicked: () => {},
    }
  }

  // Get content and other data using utility functions
  const messageContent = getResponseContent(answer)
  const followupQuestions = getFollowupQuestions(answer)
  const documentReferences = getDocumentReferences(answer)
  const shouldFormatDocumentIntelligence = sessionType === KNOWN_SESSION_TYPES.Translator
  const renderedContent = useMemo(() => {
    if (!messageContent || !shouldFormatDocumentIntelligence) {
      return messageContent
    }
    return formatDocumentIntelligenceContent(messageContent)
  }, [messageContent, shouldFormatDocumentIntelligence])

  // Dash canvas already compiles yaml-render / yaml-patch from the raw
  // message. Chat must not show that wire format as a growing code block
  // (SQL inside `_sql: |` looks clipped mid-stream). Strip fences only
  // here — never in dash-page spec extraction.
  const dashDisplayMarkdown = useMemo(() => {
    if (!isDashChatModule(moduleName) || !renderedContent) {
      return renderedContent || ""
    }
    const { displayText, fences } = stripDashSpecFences(renderedContent)
    const statusKey = dashSpecStatusI18nKey(fences, !isStreaming)
    if (!statusKey) return displayText
    const status = `*${t(statusKey)}*`
    const body = displayText.trimEnd()
    return body ? `${body}\n\n${status}` : status
  }, [moduleName, renderedContent, t, isStreaming])

  // Mirror the artifact store's ingest key (`${sessionId}:${index}`) so display
  // links for artifacts without an `identifier` resolve to the same store key.
  const artifactMessageScope =
    sessionId !== undefined && index !== undefined ? `${sessionId}:${String(index)}` : undefined
  const parsedAnswer = useMemo(() => {
    const parsed = parseAnswerToHtml({
      answer: isDashChatModule(moduleName) ? dashDisplayMarkdown : renderedContent || "",
      isStreaming,
      noCitation,
      citationSection: citationSection ?? "",
      messageIndex: index,
      messageScope: artifactMessageScope,
    })
    return parsed
  }, [
    dashDisplayMarkdown,
    renderedContent,
    isStreaming,
    noCitation,
    citationSection,
    index,
    artifactMessageScope,
    moduleName,
  ])

  // Update citations in store after render (avoid setState during render)
  useEffect(() => {
    const nonUrlCitations = parsedAnswer.citations.filter(
      (citation) => !citation.startsWith("https://") && !citation.startsWith("http://")
    )
    addCitations(nonUrlCitations)
    if (documents?.length) {
      mergeCitationLibraryDocuments(documents)
    }
  }, [parsedAnswer.citations, documents, addCitations, mergeCitationLibraryDocuments])

  async function handleCopyToClipboardClicked() {
    if (answerTextRef.current) {
      const rawText = answerTextRef.current.innerText
      await navigator.clipboard.writeText(rawText)
      toast.success(t("answer.copy_answer_success"))
    }
  }

  async function handleCopyRawToClipboardClicked() {
    if (messageContent) {
      await navigator.clipboard.writeText(messageContent)
      toast.success(t("answer.copy_raw_success"))
    }
  }

  async function handleDownloadClicked(useTemplate: boolean = false) {
    // standard filename
    const fallbackFilename = moduleName ? `${moduleName} Response` : "Response"
    let outputFilename = fallbackFilename

    // translation filename
    if (targetLanguage) {
      outputFilename = `${t("answer.translation")}-${targetLanguage}`
    }

    // custom filename
    if (customFileName) {
      outputFilename = customFileName + " Rapport"
    }

    // only replace generic fallback with session title
    // Fetch the current session title at download time to get the latest name
    const currentSessionTitle = effectiveSessionId
      ? chatServiceRef.current.getSessionById(effectiveSessionId)?.title
      : undefined
    if (outputFilename === fallbackFilename && currentSessionTitle) {
      outputFilename = DOWNLOAD_FILENAME_PREFIX + currentSessionTitle
    }

    outputFilename = getSafeFilename(outputFilename, fallbackFilename)

    const outputFormat = "docx" // make this dynamic when more formats are supported
    const safeOutputFilename = ensureFilenameLimit(outputFilename, outputFormat)

    // Render mermaid diagrams as images if present
    let contentForDownload = messageContent
    if (hasMermaidBlocks(messageContent)) {
      try {
        contentForDownload = await replaceMermaidWithImages(messageContent)
      } catch (error) {
        logger.warn("Failed to render mermaid diagrams for download:", error as Error)
      }
    }

    // Convert messageContent from Markdown to HTML
    const md = new MarkdownIt({ html: true })
    const inputFormat = targetLanguage ? "html" : "md"
    const processedContent = replaceCitationsWithLinks(contentForDownload, inputFormat)
    const renderedHtml = md.render(processedContent)
    // Wrap the rendered HTML with charset declaration
    const htmlContent = `
<!DOCTYPE html>
<html>
    <head>
        <meta charset="utf-8">
    </head>
    <body>
        ${renderedHtml}
    </body>
</html>`.trim()

    try {
      // Translator uses html format
      const responseText = targetLanguage ? htmlContent : processedContent
      const inputFormat = targetLanguage ? "html" : "md"

      // Download the file from the backend
      const response = await downloadResponseApi(
        settingsService.token,
        responseText,
        inputFormat,
        outputFormat,
        safeOutputFilename,
        useTemplate,
        effectiveSessionId
      )

      const blob = await response.blob()
      saveAs(blob, `${safeOutputFilename}.${outputFormat}`)
    } catch (error) {
      logger.warn(
        "Backend file download failed, using frontend generated file instead. Error:\n",
        error as Error
      )

      try {
        const docxBlob = (await asBlob(htmlContent)) as Blob
        saveAs(docxBlob, `${safeOutputFilename}.docx`)
        // The fallback is a plain HTML-to-DOCX conversion without the company
        // template or images. Say so instead of handing over a file that looks
        // like the real export (#2969). Only after the save actually happened.
        toast.error(t("answer.download_backend_failed"))
      } catch (error) {
        logger.error("Error generating DOCX file:", error as Error)
        toast.error(t("answer.download_failed"))
      } finally {
        logger.debug("Download complete")
      }
      return
    }
  }

  return {
    answerTextRef,
    parsedAnswer,
    followupQuestions,
    documents,
    documentReferences,
    citationSection,
    handleCopyRawToClipboardClicked,
    handleCopyToClipboardClicked,
    handleDownloadClicked,
  }
}
